import { useEffect, useRef, useState } from "react";
import { apiBlobRequest, apiRequest } from "../../shared/api/client";
import { dateLabel } from "./production-report-format";
import { addDays } from "./production-workspace-model";
import { endpoint, query } from "./production-report-selection";
import type { Selection } from "./production-report-selection";

type Dependencies = { applied: Selection | null; loading: boolean; today: string; notify: (message: string) => void };
export function useProductionReportExportsWorkspace({ applied, loading, today, notify }: Dependencies) {
  const [downloading, setDownloading] = useState<"xlsx" | null>(null);
  const [sharing, setSharing] = useState(false);
  const downloadLock = useRef(false), shareLock = useRef(false), mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const download = async (format: "xlsx") => {
    if (!applied || loading || downloadLock.current || !mounted.current) return;
    downloadLock.current = true; setDownloading(format);
    let objectUrl: string | null = null;
    try {
      const blob = await apiBlobRequest(`${endpoint}.${format}?${query(applied)}`);
      if (!mounted.current) return;
      objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl; link.download = `uretim-kaynaklari-${applied.from}-${applied.to}.xlsx`; link.click();
      const completedUrl = objectUrl;
      window.setTimeout(() => URL.revokeObjectURL(completedUrl), 1000); objectUrl = null;
      notify("Excel indirmesi başlatıldı.");
    } catch (caught) {
      if (mounted.current) notify(caught instanceof Error ? caught.message : "Excel indirilemedi.");
    } finally {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      downloadLock.current = false;
      if (mounted.current) setDownloading(null);
    }
  };
  const openDailyReport = async () => {
    if (shareLock.current || !mounted.current) return;
    shareLock.current = true; setSharing(true);
    try {
      const productionDate = addDays(today, -1);
      const result = await apiRequest<{ shareUrl: string }>("/api/production/reports/daily-snapshots", { method: "POST", body: JSON.stringify({ productionDate }) });
      if (!mounted.current) return;
      const target = new URL(result.shareUrl, window.location.origin);
      window.open(target.toString(), "_blank", "noopener,noreferrer");
      notify(`${dateLabel(productionDate)} üretim raporu hazır.`);
    } catch (caught) {
      if (mounted.current) notify(caught instanceof Error ? caught.message : "Üretim raporu açılamadı.");
    } finally {
      shareLock.current = false;
      if (mounted.current) setSharing(false);
    }
  };
  return { downloading, sharing, download, openDailyReport };
}
